// Per-user app settings — Firebase RTDB at /userSettings/$uid.
//
// One record per Firebase user. Holds Justin's (and any future user's) personal
// preferences for the app: default quote tiers, margin warning threshold,
// default job type, collar defaults overrides, and Jemma capability grants.
//
// Mirrors crm.ts patterns:
//   - All async; await awaitFirebaseUser() before any read/write
//   - Writes go through stripUndefinedDeep so Firebase never sees undefined
//   - getUserSettings returns {} when no record exists yet (first-run friendly)
//
// Settings are PER USER, not per shop — we use auth.uid as the key. If/when
// KMF grows past Justin + partner and we add a shop-wide settings layer,
// that goes under /shopSettings/$shopId and merges UNDER this (user always
// wins for personal stuff like tiers / job type; shop wins for things like
// hourly rate caps, security flags).

import {
  ref,
  get,
  set,
  onValue,
  type DatabaseReference,
} from 'firebase/database';
import { db, awaitFirebaseUser, currentFirebaseUser } from './firebase';
import { stripUndefinedDeep } from './crmValidators';
import { COLLAR_DEFAULTS } from './collarMath';
import type { JobType } from './crmTypes';

// =====================================================
// Types
// =====================================================

/**
 * Shape of the editable collar defaults. We derive this from COLLAR_DEFAULTS
 * rather than re-typing the fields — keeps the two in sync if the source
 * grows new keys. sheetPresets is intentionally omitted from user overrides
 * for now (UI complexity not worth the rarely-used customization).
 */
export interface CollarDefaultsShape {
  feedIpm: number;
  doc: number;
  spacing: number;
  sheetLen: number;
  sheetWid: number;
  hourlyRate: number;
  loadUnloadMin: number;
  screwDownMin: number;
}

export interface UserSettings {
  // ───── General ─────
  /** Default tier ladder for new parts / quotes (e.g. [10, 50, 100]). */
  defaultQuoteTiers?: number[];
  /** %, 0-100 — margin below this surfaces a warning on quote review. */
  marginWarningThreshold?: number;
  /** Pre-selected job type on the new-quote modal. */
  defaultJobType?: JobType;

  // ───── Collar defaults overrides ─────
  /** Per-field override on COLLAR_DEFAULTS. Read via getCollarDefaults(). */
  collarDefaults?: Partial<CollarDefaultsShape>;

  // ───── Jemma capability grants ─────
  /**
   * Map of capabilityId → granted. Absent key means "use the capability's
   * defaultGranted value". Explicit `false` means "user revoked the default".
   * See jemmaCapabilities.ts for the registry of valid IDs.
   */
  jemmaGrants?: Record<string, boolean>;
}

// =====================================================
// Storage helpers
// =====================================================

/**
 * RTDB ref for the current signed-in user's settings record. Throws if no one
 * is signed in — callers should await awaitFirebaseUser() first (every public
 * function in this file already does).
 */
// =====================================================
// Jemma grant key encoding
// =====================================================
//
// Firebase RTDB rejects '.', '#', '$', '/', '[', ']' in object keys. Our
// capability IDs use dot-namespacing ('brain.chat', 'email.send_draft'),
// which means we can't write them as raw keys under jemmaGrants. We encode
// '.' → '__' (double-underscore, since underscore alone is ambiguous with
// IDs that already contain it like 'email.send_draft'). Encoding is
// applied at the storage boundary only — every caller of getUserSettings /
// observeUserSettings sees the original dot-form IDs.

const GRANT_KEY_DOT_TOKEN = '__';

function encodeGrantKey(id: string): string {
  return id.replace(/\./g, GRANT_KEY_DOT_TOKEN);
}
function decodeGrantKey(encoded: string): string {
  return encoded.split(GRANT_KEY_DOT_TOKEN).join('.');
}
function encodeGrants(
  grants: Record<string, boolean> | undefined,
): Record<string, boolean> | undefined {
  if (!grants) return grants;
  const out: Record<string, boolean> = {};
  for (const [k, v] of Object.entries(grants)) out[encodeGrantKey(k)] = v;
  return out;
}
function decodeGrants(
  encoded: Record<string, boolean> | undefined,
): Record<string, boolean> | undefined {
  if (!encoded) return encoded;
  const out: Record<string, boolean> = {};
  for (const [k, v] of Object.entries(encoded)) out[decodeGrantKey(k)] = v;
  return out;
}

function decodeSettings(s: UserSettings): UserSettings {
  if (!s.jemmaGrants) return s;
  return { ...s, jemmaGrants: decodeGrants(s.jemmaGrants) };
}

function settingsRef(): DatabaseReference {
  const uid = currentFirebaseUser()?.uid;
  if (!uid) {
    throw new Error(
      'userSettings: no signed-in user. Wait for awaitFirebaseUser() before reading or writing.',
    );
  }
  return ref(db, `userSettings/${uid}`);
}

// =====================================================
// Public API
// =====================================================

/**
 * Fetch the current user's settings. Returns {} if the record doesn't exist
 * yet (first-run case). Never throws on "missing" — only on transport errors.
 */
export async function getUserSettings(): Promise<UserSettings> {
  await awaitFirebaseUser();
  const snap = await get(settingsRef());
  if (!snap.exists()) return {};
  return decodeSettings(snap.val() as UserSettings);
}

/**
 * Merge-write a patch into the current user's settings. Reads existing,
 * shallow-merges the patch, sanitizes, writes whole record back.
 *
 * Shallow merge — caller passes the entire `collarDefaults` object if they
 * want to update one field within it (mirrors how createX/updateX work in
 * crm.ts and avoids accidental nested-key wipes).
 */
export async function updateUserSettings(patch: Partial<UserSettings>): Promise<void> {
  await awaitFirebaseUser();
  const eRef = settingsRef();
  const snap = await get(eRef);
  // Existing record on disk has ENCODED grant keys; decode for the merge so
  // the patch (which uses original dot-form IDs) lines up correctly.
  const existing: UserSettings = snap.exists()
    ? decodeSettings(snap.val() as UserSettings)
    : {};
  const merged: UserSettings = { ...existing, ...patch };
  // Encode jemmaGrants keys back to Firebase-safe form right before write.
  const forWrite: UserSettings = {
    ...merged,
    jemmaGrants: encodeGrants(merged.jemmaGrants),
  };
  // stripUndefinedDeep handles the nested objects (collarDefaults, jemmaGrants)
  // without us having to enumerate them — Firebase rejects undefined anywhere.
  const sanitized = stripUndefinedDeep(forWrite as Record<string, unknown>, 'updateUserSettings');
  await set(eRef, sanitized);
}

/**
 * Subscribe to live updates. The callback fires immediately with the current
 * value, then again whenever the record changes (any tab, any device).
 * Returns the unsubscribe fn — call it on effect cleanup.
 *
 * Async because we have to await auth readiness before we can know which
 * uid to subscribe under. The returned unsub is safe to call even if the
 * subscription was never actually attached (Firebase's onValue returns a
 * noop-able unsub).
 */
export async function observeUserSettings(
  cb: (s: UserSettings) => void,
): Promise<() => void> {
  await awaitFirebaseUser();
  const eRef = settingsRef();
  // onValue returns an Unsubscribe function directly — wrap to keep our
  // shape consistent (no leaking firebase types to callers).
  const unsub = onValue(eRef, (snap) => {
    cb(snap.exists() ? decodeSettings(snap.val() as UserSettings) : {});
  });
  return () => unsub();
}

/**
 * Merged collar defaults: hardcoded COLLAR_DEFAULTS overlaid with the user's
 * per-field overrides. Callers (CollarNestingStep, CollarCutStep, etc.) should
 * use this instead of importing COLLAR_DEFAULTS directly so user changes take
 * effect everywhere.
 *
 * Per-field override pattern (not "all-or-nothing") lets Justin nudge just
 * feedIpm without having to re-enter every other default.
 */
export async function getCollarDefaults(): Promise<CollarDefaultsShape> {
  const s = await getUserSettings();
  const override = s.collarDefaults ?? {};
  return {
    feedIpm: override.feedIpm ?? COLLAR_DEFAULTS.feedIpm,
    doc: override.doc ?? COLLAR_DEFAULTS.doc,
    spacing: override.spacing ?? COLLAR_DEFAULTS.spacing,
    sheetLen: override.sheetLen ?? COLLAR_DEFAULTS.sheetLen,
    sheetWid: override.sheetWid ?? COLLAR_DEFAULTS.sheetWid,
    hourlyRate: override.hourlyRate ?? COLLAR_DEFAULTS.hourlyRate,
    loadUnloadMin: override.loadUnloadMin ?? COLLAR_DEFAULTS.loadUnloadMin,
    screwDownMin: override.screwDownMin ?? COLLAR_DEFAULTS.screwDownMin,
  };
}
